import React, { useEffect } from 'react';
import { t } from 'Src/i18n';
import { useFormikContext } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { capitalize } from 'Utils/string/stringUtils';
import { getCityTransportsSelector } from 'Store/booking/booking.selector';
import { fetchTransportStations } from 'Store/booking/transport-stations/transportStations.action';
import { useAppContext } from 'Context/ReactAppContext';
import {
  FLIGHT_CLASS_AVAILABLE_TO_SEARCH,
  TRAIN_CLASS_AVAILABLE_TO_SEARCH,
  TRANSPORT_CLASS_TO_I18N,
} from 'Components/widgets/transport-details/transportUtils';
import { FormikInput } from 'Components/frameworks/formik/FormikInput';
import { InputSwitch } from 'Components/frameworks/form-inputs/input-switch/InputSwitch';
import { makeWidgetProps, BucketFormikInput } from 'Src/types/widget-helpers';
type AdditionalTransportAdvancedSearchFormFieldsProps = {
  proposal: ProposalWithTransport;
};

function getCityTransportsByGroupName({
  data = {} as Record<string, unknown>,
  cityId,
}: {
  data?: Record<string, unknown>;
  cityId?: string;
}) {
  const {
    airports = [],
    trainStations = [],
    ports = [],
  } = data as {
    airports?: Array<{ name: string }>;
    trainStations?: Array<{ label: string }>;
    ports?: Array<{ label: string }>;
  };
  return {
    previousCityId: cityId,
    cityTransports: [
      {
        groupName: t('booking.transport.departure.airport'),
        groupOptions: airports.map((airport) => ({ ...airport, name: capitalize(airport.name) })),
      },
      {
        groupName: t('booking.transport.departure.trainStation'),
        groupOptions: trainStations.map((trainStation) => ({
          ...trainStation,
          name: capitalize(trainStation.label),
        })),
      },
      {
        groupName: t('booking.transport.departure.port'),
        groupOptions: ports.map((port) => ({ ...port, name: capitalize(port.label) })),
      },
    ],
  };
}

function getTransportClassesByGroupName(hasPlane: boolean, hasTrain: boolean) {
  return {
    transportClasses: [
      {
        groupName: t('common.transport.planeLabel'),
        groupOptions: FLIGHT_CLASS_AVAILABLE_TO_SEARCH.map((value) => ({
          value,
          name: t(`common.transport.transportClass.${TRANSPORT_CLASS_TO_I18N[value]}`),
          isDisabled: !hasPlane,
        })),
      },
      {
        groupName: t('common.transport.trainLabel'),
        groupOptions: TRAIN_CLASS_AVAILABLE_TO_SEARCH.map((value) => ({
          value,
          name: t(`common.transport.transportClass.${TRANSPORT_CLASS_TO_I18N[value]}`),
          isDisabled: !hasTrain,
        })),
      },
    ],
  };
}

export const AdditionalTransportAdvancedSearchFormFields = ({
  proposal,
}: AdditionalTransportAdvancedSearchFormFieldsProps) => {
  const { values, setFieldValue } = useFormikContext<{ directFlights: boolean }>();
  const appContext = useAppContext();
  const dispatch = useDispatch();
  const travelSections = proposal.transports?.inbound?.travelSections;
  const flightDeparture = travelSections?.[0]?.departure;
  const hasFlight = travelSections?.some(
    (travelSection) => travelSection?.transport.type === 'PLANE',
  );
  const hasTrain = travelSections?.some(
    (travelSection) => travelSection?.transport.type === 'TRAIN',
  );
  const { cityTransports, previousCityId } = getCityTransportsByGroupName(
    useSelector(getCityTransportsSelector),
  );
  const { transportClasses } = getTransportClassesByGroupName(hasFlight, hasTrain);
  useEffect(() => {
    if (previousCityId !== flightDeparture?.cityId) {
      dispatch(fetchTransportStations(flightDeparture?.cityId, appContext));
    }
  }, []);
  return (
    <div className="block" data-testid="AdditionalTransportAdvancedSearchFormFields">
      <div className="flex items-center justify-between py-10">
        <span className="block leading-1">{t('booking.transport.criteria.directFlights')}</span>
        <InputSwitch
          isChecked={values.directFlights}
          value={values.directFlights}
          onChange={(_name, isChecked) => setFieldValue('directFlights', isChecked)}
          isSwitch
          name="directFlights"
          inputTestId="directFlights"
        />
      </div>
      <div className="w-full py-10">
        <FormikInput
          {...makeWidgetProps<React.ComponentProps<typeof BucketFormikInput>>({
            className: 'capitalize',
            dataTestId: 'departure',
            label: t('booking.transport.criteria.departure'),
            name: 'departure',
            options: cityTransports,
            withOptionGroup: true,
            type: 'select',
          })}
        />
      </div>
      <div className="w-full py-10">
        <FormikInput
          {...makeWidgetProps<React.ComponentProps<typeof BucketFormikInput>>({
            dataTestId: 'flightClass',
            label: t('booking.transport.criteria.class'),
            name: 'flightClass',
            options: transportClasses,
            withOptionGroup: true,
            type: 'select',
          })}
        />
      </div>
    </div>
  );
};
